<template>
  <div class="automations-list-root">
    <div v-if="automations.length === 0" class="automations-empty">
      <div class="automations-empty-icon">
        <AutomationIcon />
      </div>
      <p class="automations-empty-title">No automations yet</p>
      <p class="automations-empty-desc">
        Automations run tasks on a schedule or on demand, no manual work needed.
      </p>
    </div>
    <div v-else class="settings-table">
      <div v-if="waitingAutomations.length > 0" class="section-header">
        Waiting for input
      </div>
      <div class="settings-table-header automation-table-row">
        <button class="col-name col-header" @click="setSort('name')">
          Name
          <ArrowDownIcon
            v-if="sortKey === 'name'"
            class="sort-icon"
            :class="{ 'sort-icon--asc': sortDir === 'asc' }"
          />
        </button>
        <button class="col-trigger col-header" @click="setSort('trigger')">
          Trigger
          <ArrowDownIcon
            v-if="sortKey === 'trigger'"
            class="sort-icon"
            :class="{ 'sort-icon--asc': sortDir === 'asc' }"
          />
        </button>
        <button class="col-secondary col-header" @click="setSort('secondary')">
          {{ showAgentColumn ? "Agent" : "Steps" }}
          <ArrowDownIcon
            v-if="sortKey === 'secondary'"
            class="sort-icon"
            :class="{ 'sort-icon--asc': sortDir === 'asc' }"
          />
        </button>
        <div class="col-actions" />
      </div>
      <template
        v-for="(automation, index) in waitingAutomations"
        :key="automation.id"
      >
        <div v-if="index > 0" class="row-separator" />
        <div
          class="automation-row automation-table-row data-row data-row--waiting"
          role="button"
          tabindex="0"
          @click="$emit('navigate', automation.id)"
          @contextmenu.prevent.stop="showContextMenu(automation, $event)"
        >
          <div class="col-name">
            <span class="automation-name">{{ automation.name }}</span>
          </div>

          <div class="col-trigger">
            <template v-if="runningMap.get(automation.id)">
              <span class="automation-running-dot" />
              <span class="automation-running-label">
                Running · step
                {{ runningMap.get(automation.id)!.step.index + 1 }}/{{
                  runningMap.get(automation.id)!.steps
                }}
              </span>
            </template>
            <span
              v-else-if="triggerLabel(automation.trigger)"
              class="automation-trigger-badge"
              :class="`automation-trigger-badge--${automation.trigger?.trigger}`"
            >
              {{ triggerLabel(automation.trigger) }}
            </span>
            <span
              v-else
              class="automation-trigger-badge automation-trigger-badge--manual"
              >Manual</span
            >
          </div>

          <div class="col-secondary">
            <template v-if="showAgentColumn">
              <template v-if="automation.agentName">
                <div
                  class="automation-agent-avatar"
                  @click.stop="
                    automation.agentId &&
                    $emit('navigateAgent', automation.agentId)
                  "
                >
                  <img
                    v-if="automation.agentAvatar"
                    :src="automation.agentAvatar"
                    :alt="automation.agentName"
                  />
                  <span v-else class="automation-agent-avatar-initial">
                    {{ automation.agentName.charAt(0) }}
                  </span>
                </div>
                <span
                  class="automation-agent-name automation-agent-name--link"
                  @click.stop="
                    automation.agentId &&
                    $emit('navigateAgent', automation.agentId)
                  "
                  >{{ automation.agentName }}</span
                >
              </template>
              <span v-else class="automation-step-count--empty">—</span>
            </template>
            <template v-else>
              <span
                v-if="automation.stepCount != null && automation.stepCount > 0"
                class="automation-step-count"
              >
                {{ automation.stepCount }}
                {{ automation.stepCount === 1 ? "step" : "steps" }}
              </span>
              <span
                v-else
                class="automation-step-count automation-step-count--empty"
                >—</span
              >
            </template>
          </div>

          <div class="col-actions">
            <ButtonMenu placement="bottom-end" @click.stop>
              <template #btn>
                <BaseButton kind="auxiliary" type="tertiary">
                  <template #icon>
                    <MoreIcon />
                  </template>
                </BaseButton>
              </template>
              <template #content>
                <BaseButton
                  kind="auxiliary"
                  type="tertiary"
                  text="Edit"
                  is-expanded
                  @click="$emit('navigate', automation.id)"
                >
                  <template #icon><EditIcon /></template>
                </BaseButton>
                <BaseButton
                  kind="auxiliary"
                  type="tertiary"
                  text="Rename"
                  is-expanded
                  @click="$emit('rename', automation.id, automation.name)"
                >
                  <template #icon><RenameIcon /></template>
                </BaseButton>
                <BaseButton
                  kind="auxiliary"
                  type="tertiary"
                  text="Run"
                  is-expanded
                  @click="$emit('run', automation.id)"
                >
                  <template #icon><AutomationIcon /></template>
                </BaseButton>
                <BaseButton
                  kind="auxiliary"
                  type="tertiary"
                  :text="automation.enabled ? 'Disable' : 'Enable'"
                  is-expanded
                  :state="
                    disablingAutomationId === automation.id
                      ? 'loading'
                      : 'default'
                  "
                  @click="
                    automation.enabled
                      ? $emit('disable', automation.id)
                      : $emit('enable', automation.id)
                  "
                >
                  <template #icon>
                    <PauseFillIcon v-if="automation.enabled" />
                    <StartIcon v-else />
                  </template>
                </BaseButton>
                <BaseButton
                  kind="auxiliary"
                  type="tertiary"
                  text="Duplicate"
                  is-expanded
                  @click="duplicateAutomation(automation)"
                >
                  <template #icon><DuplicateIcon /></template>
                </BaseButton>
                <BaseButton
                  kind="auxiliary"
                  type="tertiary"
                  tint="red"
                  text="Delete"
                  is-expanded
                  :state="
                    deletingAutomationId === automation.id
                      ? 'loading'
                      : 'default'
                  "
                  @click="$emit('delete', automation.id)"
                >
                  <template #icon><DeleteIcon /></template>
                </BaseButton>
              </template>
            </ButtonMenu>
          </div>
        </div>
      </template>
      <div
        v-if="waitingAutomations.length > 0 && sortedAutomations.length > 0"
        class="row-separator"
      />
      <template
        v-for="(automation, index) in sortedAutomations"
        :key="automation.id"
      >
        <div v-if="index > 0" class="row-separator" />
        <div
          class="automation-row automation-table-row data-row"
          role="button"
          tabindex="0"
          @click="$emit('navigate', automation.id)"
          @contextmenu.prevent.stop="showContextMenu(automation, $event)"
        >
          <div class="col-name">
            <span class="automation-name">{{ automation.name }}</span>
          </div>

          <div class="col-trigger">
            <template v-if="runningMap.get(automation.id)">
              <span class="automation-running-dot" />
              <span class="automation-running-label">
                Running · step
                {{ runningMap.get(automation.id)!.step.index + 1 }}/{{
                  runningMap.get(automation.id)!.steps
                }}
              </span>
            </template>
            <span
              v-else-if="!automation.enabled"
              class="automation-trigger-badge automation-trigger-badge--suspended"
              >Disabled</span
            >
            <span
              v-else-if="triggerLabel(automation.trigger)"
              class="automation-trigger-badge"
              :class="`automation-trigger-badge--${automation.trigger?.trigger}`"
            >
              {{ triggerLabel(automation.trigger) }}
            </span>
            <span
              v-else
              class="automation-trigger-badge automation-trigger-badge--manual"
              >Manual</span
            >
          </div>

          <div class="col-secondary">
            <template v-if="showAgentColumn">
              <template v-if="automation.agentName">
                <div
                  class="automation-agent-avatar"
                  @click.stop="
                    automation.agentId &&
                    $emit('navigateAgent', automation.agentId)
                  "
                >
                  <img
                    v-if="automation.agentAvatar"
                    :src="automation.agentAvatar"
                    :alt="automation.agentName"
                  />
                  <span v-else class="automation-agent-avatar-initial">
                    {{ automation.agentName.charAt(0) }}
                  </span>
                </div>
                <span
                  class="automation-agent-name automation-agent-name--link"
                  @click.stop="
                    automation.agentId &&
                    $emit('navigateAgent', automation.agentId)
                  "
                  >{{ automation.agentName }}</span
                >
              </template>
              <span v-else class="automation-step-count--empty">—</span>
            </template>
            <template v-else>
              <span
                v-if="automation.stepCount != null && automation.stepCount > 0"
                class="automation-step-count"
              >
                {{ automation.stepCount }}
                {{ automation.stepCount === 1 ? "step" : "steps" }}
              </span>
              <span
                v-else
                class="automation-step-count automation-step-count--empty"
                >—</span
              >
            </template>
          </div>

          <div class="col-actions">
            <ButtonMenu placement="bottom-end" @click.stop>
              <template #btn>
                <BaseButton kind="auxiliary" type="tertiary">
                  <template #icon>
                    <MoreIcon />
                  </template>
                </BaseButton>
              </template>
              <template #content>
                <BaseButton
                  kind="auxiliary"
                  type="tertiary"
                  text="Edit"
                  is-expanded
                  @click="$emit('navigate', automation.id)"
                >
                  <template #icon><EditIcon /></template>
                </BaseButton>
                <BaseButton
                  kind="auxiliary"
                  type="tertiary"
                  text="Rename"
                  is-expanded
                  @click="$emit('rename', automation.id, automation.name)"
                >
                  <template #icon><RenameIcon /></template>
                </BaseButton>
                <BaseButton
                  kind="auxiliary"
                  type="tertiary"
                  text="Run"
                  is-expanded
                  :state="automationRunActionState(automation)"
                  @click="$emit('run', automation.id)"
                >
                  <template #icon><AutomationIcon /></template>
                </BaseButton>
                <BaseButton
                  kind="auxiliary"
                  type="tertiary"
                  :text="automation.enabled ? 'Disable' : 'Enable'"
                  is-expanded
                  :state="
                    disablingAutomationId === automation.id
                      ? 'loading'
                      : 'default'
                  "
                  @click="
                    automation.enabled
                      ? $emit('disable', automation.id)
                      : $emit('enable', automation.id)
                  "
                >
                  <template #icon>
                    <PauseFillIcon v-if="automation.enabled" />
                    <StartIcon v-else />
                  </template>
                </BaseButton>
                <BaseButton
                  kind="auxiliary"
                  type="tertiary"
                  text="Duplicate"
                  is-expanded
                  @click="duplicateAutomation(automation)"
                >
                  <template #icon><DuplicateIcon /></template>
                </BaseButton>
                <BaseButton
                  kind="auxiliary"
                  type="tertiary"
                  tint="red"
                  text="Delete"
                  is-expanded
                  :state="
                    deletingAutomationId === automation.id
                      ? 'loading'
                      : 'default'
                  "
                  @click="$emit('delete', automation.id)"
                >
                  <template #icon><DeleteIcon /></template>
                </BaseButton>
              </template>
            </ButtonMenu>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from "vue";

import type {
  AiAutomationRunning,
  AiAutomationSimpleTrigger,
  AutomationId,
  OrgId,
} from "@ctrl/controller-rpc";

import { automationActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import type { AutomationObject } from "@/components/context-menu/context-actions";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import ArrowDownIcon from "@/components/icons/ArrowDownIcon.vue";
import AutomationIcon from "@/components/icons/AutomationIcon.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import DuplicateIcon from "@/components/icons/DuplicateIcon.vue";
import EditIcon from "@/components/icons/EditIcon.vue";
import MoreIcon from "@/components/icons/MoreIcon.vue";
import PauseFillIcon from "@/components/icons/PauseFillIcon.vue";
import RenameIcon from "@/components/icons/RenameIcon.vue";
import StartIcon from "@/components/icons/StartIcon.vue";
import * as inject from "@/components/inject";

export type AutomationsListItem = {
  id: AutomationId;
  name: string;
  agentId?: string;
  agentName?: string;
  agentAvatar?: string;
  trigger?: AiAutomationSimpleTrigger;
  stepCount?: number;
  enabled?: boolean;
};

function automationRunActionState(automation: AutomationsListItem) {
  return automation.enabled === false ? "disabled" : "default";
}

function triggerLabel(trigger?: AiAutomationSimpleTrigger): string | null {
  if (!trigger || trigger.trigger === "manual") return null;
  if (trigger.trigger === "recurrent") {
    const { hours, minutes } = trigger.daytime;
    const h = hours % 12 || 12;
    const m = (minutes ?? 0).toString().padStart(2, "0");
    const ampm = hours < 12 ? "AM" : "PM";
    return `Scheduled · ${h}:${m} ${ampm}`;
  }
  if (trigger.trigger === "commit_pattern") return "Reaction";
  return null;
}

export default defineComponent({
  name: "AutomationsList",
  components: {
    ArrowDownIcon,
    AutomationIcon,
    BaseButton,
    ButtonMenu,
    DeleteIcon,
    DuplicateIcon,
    EditIcon,
    MoreIcon,
    PauseFillIcon,
    RenameIcon,
    StartIcon,
  },
  props: {
    automations: {
      type: Array as PropType<AutomationsListItem[]>,
      required: true,
    },
    /**
     * Organization owning the listed automations; scopes the right-click
     * context-menu actions. The menu is not offered when unset (skeleton
     * states).
     */
    organizationId: {
      type: String as PropType<OrgId | null>,
      default: null,
    },
    deletingAutomationId: {
      type: String as PropType<AutomationId | null>,
      default: null,
    },
    disablingAutomationId: {
      type: String as PropType<AutomationId | null>,
      default: null,
    },
    running: {
      type: Array as PropType<AiAutomationRunning[]>,
      default: () => [],
    },
    showAgentColumn: {
      type: Boolean,
      default: false,
    },
    waitingAutomationIds: {
      type: Object as PropType<Set<AutomationId>>,
      default: () => new Set(),
    },
  },
  emits: {
    navigate: (_id: AutomationId) => true,
    navigateAgent: (_agentId: string) => true,
    rename: (_id: AutomationId, _currentName: string) => true,
    run: (_id: AutomationId) => true,
    disable: (_id: AutomationId) => true,
    enable: (_id: AutomationId) => true,
    delete: (_id: AutomationId) => true,
  },
  setup(props) {
    const contextMenu = inject.contextMenu();
    const controller = inject.controller();
    const automationsComposable = inject.automations();
    const router = inject.router();

    // The listed automation as a shared-action target (see
    // `automationActions` in actions.ts), or null while the organization is
    // unknown (skeleton states).
    const automationTarget = (
      automation: AutomationsListItem
    ): AutomationObject | null => {
      const organizationId = props.organizationId;
      if (!organizationId) return null;
      return {
        type: "automation",
        value: {
          organizationId,
          id: automation.id,
          name: automation.name,
          enabled: automation.enabled !== false,
        },
      };
    };

    // Right-click mirrors the row's "..." menu through the shared context
    // menu: Open, Run, Disable/Enable, Duplicate, Copy link, Delete.
    const showContextMenu = (
      automation: AutomationsListItem,
      event: MouseEvent
    ) => {
      const target = automationTarget(automation);
      if (!target) return;
      contextMenu.value.show(target, event);
    };

    // Same automation under the same agent, but disabled and named
    // "... (copy)" — the shared `automationActions.duplicate`, which then
    // navigates to the copy.
    const duplicateAutomation = (automation: AutomationsListItem) => {
      const target = automationTarget(automation);
      if (!target) return;
      automationActions.duplicate.action(target, {
        controller,
        automations: automationsComposable,
        router,
      });
    };

    const runningMap = computed(() => {
      const map = new Map<string, AiAutomationRunning>();
      for (const r of props.running) {
        map.set(r.id, r);
      }
      return map;
    });

    const waitingAutomations = computed(() =>
      props.automations.filter((a) => props.waitingAutomationIds.has(a.id))
    );

    const otherAutomations = computed(() =>
      props.automations.filter((a) => !props.waitingAutomationIds.has(a.id))
    );

    type SortKey = "name" | "trigger" | "secondary";
    const sortKey = ref<SortKey>("name");
    const sortDir = ref<"asc" | "desc">("asc");

    const setSort = (key: SortKey) => {
      if (sortKey.value === key) {
        sortDir.value = sortDir.value === "asc" ? "desc" : "asc";
      } else {
        sortKey.value = key;
        sortDir.value = "asc";
      }
    };

    function triggerSortValue(item: AutomationsListItem): string {
      const t = item.trigger;
      if (!t || t.trigger === "manual") return "0";
      if (t.trigger === "recurrent") return `1-${triggerLabel(t)}`;
      if (t.trigger === "commit_pattern") return "2";
      return "3";
    }

    const sortedAutomations = computed(() => {
      const dir = sortDir.value === "asc" ? 1 : -1;
      return [...otherAutomations.value].sort((a, b) => {
        let cmp = 0;
        if (sortKey.value === "name") {
          cmp = a.name.localeCompare(b.name);
        } else if (sortKey.value === "trigger") {
          cmp = triggerSortValue(a).localeCompare(triggerSortValue(b));
        } else {
          const aVal = props.showAgentColumn
            ? (a.agentName ?? "")
            : String(a.stepCount ?? 0).padStart(6, "0");
          const bVal = props.showAgentColumn
            ? (b.agentName ?? "")
            : String(b.stepCount ?? 0).padStart(6, "0");
          cmp = props.showAgentColumn
            ? aVal.localeCompare(bVal)
            : Number(a.stepCount ?? 0) - Number(b.stepCount ?? 0);
        }
        return cmp * dir;
      });
    });

    return {
      automationRunActionState,
      duplicateAutomation,
      showContextMenu,
      runningMap,
      waitingAutomations,
      triggerLabel,
      sortKey,
      sortDir,
      setSort,
      sortedAutomations,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";
.automations-list-root {
  width: 100%;
}

// ── Empty state ──────────────────────────────────────────────────────────────

.automations-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px;
  border-radius: 12px;
  gap: 6px;
  @include themed() {
    background-color: t($bg-alt);
    border: 1px solid t($grey-10);
  }

  .automations-empty-title {
    margin: 0;
    @extend %font-body-medium;
    @include themed() {
      color: t($primary);
    }
  }

  .automations-empty-desc {
    margin: 0 0 12px;
    @extend %font-body-regular;
    max-width: 280px;
    @include themed() {
      color: t($grey-60);
    }
  }
}

// ── Table ────────────────────────────────────────────────────────────────────

.section-header {
  @extend %font-caption-medium;
  padding: 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  @include themed() {
    color: t($tertiary-100);
  }
}

$col-trigger-width: 170px;
$col-secondary-width: 100px;
$col-actions-width: 36px;

// Shared grid layout for header + rows
.automation-table-row {
  display: grid;
  grid-template-columns:
    1fr
    $col-trigger-width
    $col-secondary-width
    $col-actions-width;
  align-items: center;
  column-gap: 12px;
}

// Sort-button column headers (override shared .col-header text style with
// interactive button behaviour)
.col-header {
  all: unset;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: $border-radius-m;
  padding: 2px 4px;
  margin: -2px -4px;
  @extend %font-caption-medium;
  @include themed() {
    color: t($grey-40);
  }

  &:hover {
    @include themed() {
      color: t($grey-60);
    }
  }

  .sort-icon {
    width: 10px;
    height: 10px;
    @include transition(transform 0.15s ease);
    transform: rotate(0deg);

    &--asc {
      transform: rotate(180deg);
    }
  }
}

// ── Data Row ─────────────────────────────────────────────────────────────────

.data-row--waiting {
  @include themed() {
    background-color: t($callout-info);
  }
}

// ── Columns ──────────────────────────────────────────────────────────────────

.col-name {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.col-trigger,
.col-secondary {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.col-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

// ── Name cell ────────────────────────────────────────────────────────────────

.automation-name {
  @extend %font-body-regular;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @include themed() {
    color: t($primary);
  }
}

// ── Trigger cell ─────────────────────────────────────────────────────────────

.automation-trigger-badge {
  @extend %font-caption-medium;
  padding: 2px 7px;
  border-radius: $border-radius-s;
  white-space: nowrap;

  @include themed() {
    background-color: t($grey-05);
    color: t($grey-60);
    border: 1px solid t($grey-10);
  }

  &--suspended {
    @include themed() {
      background-color: t($grey-10);
      color: t($grey-40);
      border: 1px solid t($grey-10);
    }
  }
}

.automation-running-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  animation: pulse 1.4s ease-in-out infinite;
  @include themed() {
    background-color: t($tertiary-100);
  }
}

.automation-running-label {
  @extend %font-caption-regular;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @include themed() {
    color: t($tertiary-100);
  }
}

// ── Secondary cell (Steps or Agent) ──────────────────────────────────────────

.automation-agent-avatar {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: $border-radius-s;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .automation-agent-avatar-initial {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    @extend %font-small-medium;
    @include themed() {
      background-color: t($tertiary-50);
      color: t($bg);
    }
  }
}

.automation-agent-name {
  @extend %font-caption-regular;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @include themed() {
    color: t($grey-60);
  }

  &--link {
    border-radius: $border-radius-s;
    padding: 1px 4px;
    margin: -1px -4px;

    &:hover {
      @include themed() {
        background-color: t($bg-hover);
        color: t($primary);
      }
    }
  }
}

.automation-step-count {
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-60);
  }

  &--empty {
    @include themed() {
      color: t($grey-30);
    }
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}
</style>
